/**
 * Shared utilities used by resizable-layout handlers.
 */

import { fetchWithAuth } from "@/lib/api"
import { DEFAULT_PRODUCT_BOARD_DOCUMENT_FILE_PATH } from "@/lib/product-board-default-doc"
import {
  normalizeFolderStoragePath,
  workspaceForStoragePath,
  type WorkspaceRouteInfo,
} from "@/lib/app-routes"

/**
 * Track document view for analytics
 */
export async function trackDocumentView(
  documentId: string,
  viewSource: string,
  activeTabId: string | null
): Promise<void> {
  try {
    // Only track real documents, not special views
    if (
      documentId === 'list' ||
      documentId === 'edit-history' ||
      documentId.startsWith('view:') ||
      documentId.startsWith('basecamp-') ||
      documentId.startsWith('workspace-home-') ||
      documentId.startsWith('desk-home-')
    ) {
      return;
    }

    // Use new endpoint that handles file paths properly
    const response = await fetchWithAuth(`/api/documents/track-view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        documentId,
        viewSource,
        referrer: activeTabId,
        timestamp: new Date().toISOString()
      })
    });

    // Only log errors in development
    if (!response.ok && import.meta.env.DEV) {
      console.debug('[View Tracking] Non-critical error:', response.status);
    }
  } catch (error) {
    // Silent fail - view tracking is non-critical
    // Only log in development
    if (import.meta.env.DEV) {
      console.debug('[View Tracking] Failed:', error);
    }
  }
}

/**
 * Strip top-level API archive roots so archived docs still resolve to their
 * owning workspace (archive/hop-studios/... -> hop-studios/...).
 */
function storagePathForWorkspaceLookup(storagePath: string): string {
  const normalized = storagePath.replace(/^\/+/, '')
  if (normalized.startsWith('flownotes-app-docs/archive/')) {
    return `flownotes-app-docs/${normalized.slice('flownotes-app-docs/archive/'.length)}`
  }
  if (normalized.startsWith('archive/')) {
    return normalized.slice('archive/'.length)
  }
  return normalized
}

/**
 * Resolve workspace id from document path (Phase B tab groups).
 */
export function resolveWorkspaceForDocument(
  documentPath: string,
  workspaces: { id: string; slug?: string; contentRoot: string | null }[]
): string | undefined {
  if (!documentPath?.trim()) return undefined
  const routeInfo: WorkspaceRouteInfo[] = workspaces.map((w) => ({
    id: w.id,
    slug: w.slug ?? '',
    contentRoot: w.contentRoot,
  }))
  const storagePath = storagePathForWorkspaceLookup(
    normalizeFolderStoragePath(documentPath, routeInfo) ??
      documentPath.replace(/^\/+/, ''),
  )
  const ws = workspaceForStoragePath(storagePath, routeInfo)
  if (ws) return ws.id
  const match = workspaces.find((w) => {
    if (!w.contentRoot) return false
    const prefix = w.contentRoot.replace(/\/$/, '')
    return storagePath === prefix || storagePath.startsWith(prefix + '/')
  })
  return match?.id
}

/** Minimal tab shape for workspace visibility (avoids importing OpenTab here). */
export type TabWorkspaceResolutionInput = {
  id: string
  type: string
  workspaceId?: string | null
  /** When set, resolves owning org via content_root prefix (same as explorer folder path). */
  filePath?: string | null
  explorerData?: { folderPath: string | null }
  integrationData?: {
    workspaceSlug?: string
    organizationId?: string
    [key: string]: unknown
  }
}

const WORKSPACE_AGNOSTIC_TAB_TYPES = new Set([
  'home',
  'edit-history',
  'inbox',
  'flow-index',
  // Drive index/preview belong to the workspace of the connected Drive account
  // (Drive account picker). Do not treat as App-global.
  // inbox-thread is a workspace entity tab when workspaceId is set (2026-07-31)
  'chat-conversation',
  'graph-view',
  'search-view',
  'platform-skill',
])

/** Content path carried on tab id (v2 extensionless URLs) when filePath was not persisted. */
export function tabContentPathFromId(tabId: string): string | null {
  if (!tabId.includes('/')) return null
  if (
    tabId.startsWith('view:') ||
    tabId.startsWith('basecamp-') ||
    tabId.startsWith('integration-') ||
    tabId.startsWith('workspace-home-') ||
    tabId.startsWith('desk-home-') ||
    tabId.startsWith('inbox-thread-') ||
    tabId.startsWith('chat-conversation-')
  ) {
    return null
  }
  return tabId
}

function tabContentPathForWorkspace(tab: TabWorkspaceResolutionInput): string | null {
  if (tab.filePath) return tab.filePath
  return tabContentPathFromId(tab.id)
}

function integrationCanonicalContentPath(tab: TabWorkspaceResolutionInput): string | null {
  if (tab.type === 'integration-product-board') {
    return DEFAULT_PRODUCT_BOARD_DOCUMENT_FILE_PATH
  }
  return null
}

/**
 * Owning workspace for tab filtering: canonical integration paths, document file path,
 * explorer folder path, workspace-home id, integrationData slug, then explicit workspaceId.
 *
 * For Basecamp integration tabs created before workspaces loaded, the workspaceId may point
 * to the fallback org. integrationData.workspaceSlug (set from the deep-link URL) takes
 * precedence when it resolves to a known workspace.
 */
export function resolveTabWorkspaceId(
  tab: TabWorkspaceResolutionInput,
  workspaces: { id: string; slug?: string; contentRoot: string | null }[]
): string | undefined {
  if (isWorkspaceAgnosticTab(tab)) return undefined

  const integrationPath = integrationCanonicalContentPath(tab)
  if (integrationPath) {
    const fromIntegration = resolveWorkspaceForDocument(integrationPath, workspaces)
    if (fromIntegration) return fromIntegration
  }

  if (tab.type === 'workspace-home' && tab.id.startsWith('workspace-home-')) {
    return tab.id.slice('workspace-home-'.length)
  }

  if (tab.type === 'desk-home' && tab.id.startsWith('desk-home-')) {
    return tab.id.slice('desk-home-'.length)
  }

  if (tab.type === 'explorer' && tab.explorerData?.folderPath) {
    const fromExplorer = resolveWorkspaceForDocument(tab.explorerData.folderPath, workspaces)
    if (fromExplorer) return fromExplorer
  }

  const contentPath = tabContentPathForWorkspace(tab)
  if (contentPath) {
    const fromPath = resolveWorkspaceForDocument(contentPath, workspaces)
    if (fromPath) return fromPath
  }

  // Basecamp integration tabs: resolve from slug stored in deep-link URL
  // (takes precedence over workspaceId which may have been set from the fallback org)
  const wcSlug =
    tab.integrationData?.workspaceSlug
  if (wcSlug && workspaces.length > 0) {
    const fromSlug = workspaces.find((w) => w.slug === wcSlug)?.id
    if (fromSlug) return fromSlug
  }

  if (tab.workspaceId) return tab.workspaceId

  const orgFromIntegration = tab.integrationData?.organizationId
  if (typeof orgFromIntegration === 'string' && orgFromIntegration) {
    if (workspaces.some((w) => w.id === orgFromIntegration)) return orgFromIntegration
  }

  return undefined
}

export function isWorkspaceAgnosticTab(tab: TabWorkspaceResolutionInput): boolean {
  return WORKSPACE_AGNOSTIC_TAB_TYPES.has(tab.type)
}

/** Hide tabs whose workspace is toggled off in the sidebar switcher. */
export function isTabVisibleForActiveWorkspaces(
  tab: TabWorkspaceResolutionInput,
  workspaces: { id: string; contentRoot: string | null }[],
  activeWorkspaceIds: string[],
  activeTabId?: string | null,
): boolean {
  if (isWorkspaceAgnosticTab(tab)) return true

  const ws = resolveTabWorkspaceId(tab, workspaces)
  // Resolved workspace that is off: always hide, even if this is the active tab.
  // Only (and Hide) must park the group and let pickActiveTabForWorkspaceScope take over.
  if (ws) return activeWorkspaceIds.includes(ws)

  // No resolvable workspace: keep the active tab visible (orphan / provisional).
  if (activeTabId && tab.id === activeTabId) return true

  // Workspaces not loaded yet: keep rail stable until org list arrives.
  if (workspaces.length === 0) return true

  // Basecamp / Drive deep links often open before workspaceId is set.
  const isBasecampIntegrationTab =
    tab.type?.startsWith('integration-basecamp') ||
    (Boolean(tab.id?.startsWith('basecamp-')) && tab.type !== 'home')
  if (isBasecampIntegrationTab) return true

  const isDriveIntegrationTab =
    tab.type === 'integration-google-drive' ||
    tab.type === 'integration-google-drive-preview'
  if (isDriveIntegrationTab) return true

  if (tab.type === 'integration-nextcloud') return true

  // Scoped tab with no resolvable workspace (e.g. stale integration tab) stays hidden.
  return false
}

/** Display-only filter: tabs in inactive workspaces stay in openTabs; UI hides them. */
export function filterTabsForActiveWorkspaces<T extends TabWorkspaceResolutionInput>(
  openTabs: T[],
  workspaces: { id: string; contentRoot: string | null }[],
  activeWorkspaceIds: string[],
  activeTabId?: string | null,
): T[] {
  if (workspaces.length === 0) return openTabs
  return openTabs.filter((tab) =>
    isTabVisibleForActiveWorkspaces(tab, workspaces, activeWorkspaceIds, activeTabId),
  )
}

function visibleTabsForScope(
  openTabs: TabWorkspaceResolutionInput[],
  workspaces: { id: string; contentRoot: string | null }[],
  activeWorkspaceIds: string[],
  activeTabId?: string | null,
): TabWorkspaceResolutionInput[] {
  return openTabs.filter((tab) =>
    isTabVisibleForActiveWorkspaces(tab, workspaces, activeWorkspaceIds, activeTabId),
  )
}

/**
 * Pick which tab to show after workspace scope changes or when the current tab is hidden.
 * Prefers per-workspace last-active (localStorage) when exactly one workspace is active.
 */
export function pickActiveTabForWorkspaceScope(
  openTabs: TabWorkspaceResolutionInput[],
  workspaces: { id: string; contentRoot: string | null }[],
  activeWorkspaceIds: string[],
  lastActiveByWorkspace: Record<string, string> = {}
): string {
  const visible = visibleTabsForScope(openTabs, workspaces, activeWorkspaceIds)

  const globalHome = visible.find((t) => t.id === 'home' || t.type === 'home')

  if (activeWorkspaceIds.length === 1) {
    const wsId = activeWorkspaceIds[0]
    const remembered = lastActiveByWorkspace[wsId]
    if (remembered && visible.some((t) => t.id === remembered)) {
      const rememberedTab = openTabs.find((t) => t.id === remembered)
      if (rememberedTab?.type === 'inbox-thread' && globalHome) {
        return globalHome.id as string
      }
      return remembered
    }
    if (globalHome) {
      return globalHome.id as string
    }
    const wsHomeId = `workspace-home-${wsId}`
    if (visible.some((t) => t.id === wsHomeId)) {
      return wsHomeId
    }
  }

  for (const tab of openTabs) {
    if (visible.some((v) => v.id === tab.id)) {
      return tab.id as string
    }
  }

  if (visible.length > 0) {
    return visible[0].id as string
  }

  // Never return a tab that is hidden by the workspace switcher. Falling back to
  // openTabs[0] kept Hop (etc.) docs on screen while chat scope excluded that workspace.
  const homeInOpen = openTabs.find((t) => t.id === 'home' || t.type === 'home')
  return (homeInOpen?.id as string) ?? 'home'
}

export function isHomeTab(tab: Pick<TabWorkspaceResolutionInput, 'id' | 'type'>): boolean {
  return tab.id === 'home' || tab.type === 'home'
}

/** Home is the empty-state tab. Close is a no-op when nothing else is open. */
export function isSoleHomeTab(
  tabs: Array<Pick<TabWorkspaceResolutionInput, 'id' | 'type'>>,
  tabId?: string | null,
): boolean {
  const target = tabId
    ? tabs.find((t) => t.id === tabId)
    : tabs.find(isHomeTab)
  if (!target || !isHomeTab(target)) return false
  return tabs.every((t) => t.id === target.id || isHomeTab(t))
}

/** App-shell tabs (Home, Edit History, etc.) always render in the leftmost tab group. */
const APP_TAB_TYPE_SORT_ORDER: Record<string, number> = {
  home: 0,
  'edit-history': 1,
  'search-view': 2,
  'person-view': 2,
  inbox: 3,
  'flow-index': 3,
  'inbox-thread': 4,
  'integration-google-drive': 5,
  'graph-view': 6,
}

export function sortAppGroupTabs<T extends TabWorkspaceResolutionInput>(tabs: T[]): T[] {
  return [...tabs].sort((a, b) => {
    const ao = APP_TAB_TYPE_SORT_ORDER[a.type] ?? 50
    const bo = APP_TAB_TYPE_SORT_ORDER[b.type] ?? 50
    if (ao !== bo) return ao - bo
    if (isHomeTab(a)) return -1
    if (isHomeTab(b)) return 1
    return 0
  })
}

/** Keep canonical Home tab first in openTabs (App group order + stable id). */
export function pinHomeTabFirstInOpenTabs<T extends TabWorkspaceResolutionInput & { title?: string; tabSlotId?: string }>(
  tabs: T[],
): T[] {
  const home = tabs.find(isHomeTab)
  if (!home) return tabs
  const normalized = {
    ...home,
    id: 'home',
    type: 'home',
    title: home.title === 'Home' ? home.title : 'Home',
    workspaceId: undefined,
    filePath: undefined,
    explorerData: undefined,
  } as T
  return [normalized, ...tabs.filter((t) => !isHomeTab(t))]
}

const DEFAULT_HOME_TAB_FIELDS = {
  id: 'home',
  title: 'Home',
  type: 'home' as const,
  isDirty: false,
  tabSlotId: 'tab-main',
}

/**
 * Guarantee a Home tab exists before activating it (workspace scope recovery,
 * cold-load orphan activeTabId=home after openTabs was replaced by a deep link).
 */
export function ensureHomeTabInOpenTabs<
  T extends TabWorkspaceResolutionInput & {
    title?: string
    tabSlotId?: string
    isDirty?: boolean
  },
>(tabs: T[]): T[] {
  if (tabs.some(isHomeTab)) {
    return pinHomeTabFirstInOpenTabs(tabs)
  }
  return pinHomeTabFirstInOpenTabs([{ ...DEFAULT_HOME_TAB_FIELDS } as T, ...tabs])
}

/**
 * Chat "open document" follows the sidebar. A tab whose workspace is off
 * (ghost Hop viewer) must not become activeDocumentId or widen scope.
 * Explorer has no single document. Unscoped tabs (Home) stay eligible.
 */
export function isActiveTabDocumentInChatScope(
  tab: TabWorkspaceResolutionInput | undefined,
  workspaces: { id: string; slug?: string; contentRoot: string | null }[],
  activeWorkspaceIds: string[],
): boolean {
  if (!tab || tab.type === 'explorer') return false
  const ws = resolveTabWorkspaceId(tab, workspaces)
  if (!ws) return true
  return activeWorkspaceIds.includes(ws)
}

/** Close several tabs in one pass. Avoids one-at-a-time setState races (Open Tabs Close all). */
export function planCloseManyTabs<
  T extends TabWorkspaceResolutionInput & {
    title?: string
    tabSlotId?: string
    isDirty?: boolean
  },
>(
  openTabs: T[],
  idsToClose: string[],
  activeTabId: string | null,
): { remaining: T[]; closed: T[]; nextActiveId: string | null } {
  const idSet = new Set(idsToClose)
  const remaining = ensureHomeTabInOpenTabs(openTabs.filter((tab) => !idSet.has(tab.id)))
  const remainingIds = new Set(remaining.map((tab) => tab.id))
  const closed = openTabs.filter((tab) => idSet.has(tab.id) && !remainingIds.has(tab.id))
  const nextActiveId =
    activeTabId && remaining.some((tab) => tab.id === activeTabId)
      ? activeTabId
      : (remaining.find((tab) => tab.id === 'home' || tab.type === 'home')?.id ??
        remaining[remaining.length - 1]?.id ??
        null)
  return { remaining, closed, nextActiveId }
}
